import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useBranding } from '../hooks/useBranding';
import BrandedHeader from '../components/BrandedHeader';
import CustomerCaptureForm from '../components/CustomerCaptureForm';
import PWAInstallPrompt from "../components/PWAInstallPrompt";
import {PRIMARY_COLOR} from "../contants/Colors";

const EMOJIS = [
  { value: 1, emoji: '😠', label: 'Angry',   color: '#ef4444' },
  { value: 2, emoji: '😢', label: 'Sad',     color: '#f97316' },
  { value: 3, emoji: '😐', label: 'Neutral', color: '#94a3b8' },
  { value: 4, emoji: '😊', label: 'Happy',   color: '#22c55e' },
  { value: 5, emoji: '🤩', label: 'Excited', color: '#8b5cf6' },
];

// ─── Stage machine ──────────────────────────────────────────────────────────
// 'idle'      → staff enters bill ID
// 'choose'    → customer picks emoji or audio
// 'emoji'     → customer answers emoji questions
// 'audio'     → customer records audio
// 'done'      → thank-you screen

export default function FeedbackTakerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding(user?.tenantId);

  const [stage, setStage] = useState('idle');
  const [billId, setBillId] = useState('');
  const [session, setSession] = useState(null);
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [customerInfo, setCustomerInfo] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('new');
  const [history, setHistory] = useState([]);

  // Audio
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => { loadForms(); loadHistory(); }, []);

  const loadForms = async () => {
    try { const { data } = await api.get('/feedback-taker/forms'); setForms(data.data); } catch {}
  };
  const loadHistory = async () => {
    try { const { data } = await api.get('/feedback-taker/sessions'); setHistory(data.data); } catch {}
  };

  // ── Staff creates session ─────────────────────────────────────────────────
  const handleCreateSession = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/feedback-taker/sessions', { billId: billId.trim() || null });
      setSession(data.data);
      // auto-select first form if only one
      if (forms.length === 1) setSelectedForm(forms[0]);
      setStage('choose');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create session');
    } finally { setLoading(false); }
  };

  // ── Customer picks type ───────────────────────────────────────────────────
  const handleChooseEmoji = () => {
    if (!selectedForm) { setError('Please select a feedback form'); return; }
    setError('');
    setAnswers({});
    setStage('emoji');
  };

  const handleChooseAudio = () => {
    setError('');
    setStage('audio');
  };

  // ── Emoji submission ──────────────────────────────────────────────────────
  const setAnswer = (questionId, questionText, emojiValue) => {
    setAnswers(prev => ({ ...prev, [questionId]: { questionId, questionText, emojiValue } }));
  };

  const handleEmojiSubmit = async () => {
    const questions = selectedForm?.questions || [];
    if (Object.keys(answers).length < questions.length) {
      setError('Please answer all questions.'); return;
    }
    setError(''); setLoading(true);
    try {
      await api.post('/response/submit', {
        sessionId: session._id,
        feedbackFormId: selectedForm._id,
        answers: Object.values(answers),
        customerName: customerInfo.name || null,
        customerPhone: customerInfo.phone || null,
        customerEmail: customerInfo.email || null,
      });
      setStage('done');
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed');
    } finally { setLoading(false); }
  };

  // ── Audio recording ───────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setRecording(true);
      setAudioDuration(0);
      timerRef.current = setInterval(() => setAudioDuration(d => d + 1), 1000);
    } catch { setError('Microphone access denied.'); }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const handleAudioSubmit = async () => {
    if (!audioBlob) { setError('Please record audio first.'); return; }
    setError(''); setLoading(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'feedback.webm');
      formData.append('sessionId', session._id);
      formData.append('audioDuration', audioDuration);
      if (customerInfo.name)  formData.append('customerName',  customerInfo.name);
      if (customerInfo.phone) formData.append('customerPhone', customerInfo.phone);
      if (customerInfo.email) formData.append('customerEmail', customerInfo.email);
      await api.post('/response/submit-audio', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setStage('done');
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed');
    } finally { setLoading(false); }
  };

  // ── Reset for next customer ───────────────────────────────────────────────
  const reset = () => {
    setStage('idle'); setBillId(''); setSession(null);
    setAnswers({}); setAudioBlob(null); setAudioUrl(null);
    setAudioDuration(0); setError('');
    if (forms.length !== 1) setSelectedForm(null);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  // ────────────────────────────────────────────────────────────────────────
  const renderStaffTab = () => (
    <div>
      {/* ── IDLE: Staff enters Bill ID ── */}
      {stage === 'idle' && (
        <div className="card" style={{ maxWidth: 500 }}>
          <h3 style={{ marginBottom: 6 }}>New Feedback Session</h3>
          <p className="text-muted" style={{ fontSize: 14, marginBottom: 20 }}>
            Enter the bill ID if applicable, then hand the device to the customer.
          </p>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleCreateSession}>
            <div className="form-group">
              <label>Bill ID / Reference <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
              <input
                className="form-control"
                value={billId}
                onChange={e => setBillId(e.target.value)}
                placeholder="e.g. INV-2024-001"
                style={{ fontSize: 16 }}
              />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} type="submit" disabled={loading}>
              {loading ? 'Creating…' : '▶ Start — Hand to Customer'}
            </button>
          </form>
        </div>
      )}

      {/* ── CHOOSE: Customer picks emoji or audio ── */}
      {stage === 'choose' && (
          <div style={{maxWidth: 520}}>
            <BrandedHeader
                branding={branding}
                billId={session?.billId}
                subtitle="How would you like to share your feedback?"
            />

            {error && <div className="alert alert-error">{error}</div>}

            {/* Form selector — only shown if multiple forms */}
            {forms.length > 1 && (
                <div className="card mb-2">
                  <div className="form-group" style={{margin: 0}}>
                    <label>Select Feedback Form</label>
                    <select
                        className="form-control"
                        value={selectedForm?._id || ''}
                        onChange={e => setSelectedForm(forms.find(f => f._id === e.target.value))}
                    >
                      <option value="">-- Choose a form --</option>
                      {forms.map(f => <option key={f._id} value={f._id}>{f.title}</option>)}
                    </select>
                  </div>
                </div>
            )}

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14}}>
              <button
                  onClick={handleChooseEmoji}
                  style={{
                    background: '#fff',
                    border: '2px solid var(--border)',
                    borderRadius: 14,
                    padding: '28px 16px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all .2s'
                  }}
                  onMouseOver={e => e.currentTarget.style.borderColor = '#2563eb'}
                  onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{fontSize: 44, marginBottom: 10}}>😊</div>
                <div style={{fontWeight: 700, fontSize: 15, marginBottom: 4}}>Emoji Feedback</div>
                <div style={{fontSize: 13, color: 'var(--text-muted)'}}>Answer a few quick questions</div>
              </button>
              <button
                  onClick={handleChooseAudio}
                  style={{
                    background: '#fff',
                    border: '2px solid var(--border)',
                    borderRadius: 14,
                    padding: '28px 16px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all .2s'
                  }}
                  onMouseOver={e => e.currentTarget.style.borderColor = '#2563eb'}
                  onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{fontSize: 44, marginBottom: 10}}>🎙️</div>
                <div style={{fontWeight: 700, fontSize: 15, marginBottom: 4}}>Voice Feedback</div>
                <div style={{fontSize: 13, color: 'var(--text-muted)'}}>Record your thoughts</div>
              </button>
            </div>
            <div style={{
              fontSize: 11, color: '#94a3b8', marginTop: 20, width: '100%', textAlign: 'center'
            }}>
              Powered by{' '}
              <span>
            <a href={'https://theperceptpulse.com'} target={'_blank'}
               style={{color: PRIMARY_COLOR, textDecoration: 'none'}}>
              Percept Pulse
            </a>
          </span>
            </div>
          </div>
      )}

      {/* ── EMOJI: Customer answers questions ── */}
      {stage === 'emoji' && selectedForm && (
          <div style={{maxWidth: 560}}>
            <BrandedHeader
                branding={branding}
                billId={session?.billId}
                subtitle={selectedForm.title}
            />

            {error && <div className="alert alert-error">{error}</div>}

            {selectedForm.questions.map((q, i) => {
              const selected = answers[q._id]?.emojiValue;
              return (
                  <div key={q._id} className="card mb-2">
                    <p style={{fontWeight: 600, marginBottom: 14, fontSize: 15}}>
                      <span style={{color: branding.primaryColor, marginRight: 8}}>Q{i + 1}.</span>{q.text}
                    </p>
                    <div className="emoji-options" style={{justifyContent: 'center'}}>
                      {EMOJIS.map(e => (
                          <button
                              key={e.value}
                              type="button"
                              className={`emoji-btn ${selected === e.value ? 'selected' : ''}`}
                              style={selected === e.value ? {
                                borderColor: branding.primaryColor,
                                background: branding.primaryColor + "18"
                              } : {}}
                              onClick={() => setAnswer(q._id, q.text, e.value)}
                          >
                            {e.emoji}
                            <span style={selected === e.value ? {color: branding.primaryColor} : {}}>{e.label}</span>
                          </button>
                      ))}
                    </div>
                  </div>
              );
            })}

            <CustomerCaptureForm branding={branding} onChange={setCustomerInfo}/>

            <button
                className="btn btn-primary btn-lg"
                style={{width: '100%', justifyContent: 'center', marginTop: 12}}
                onClick={handleEmojiSubmit}
                disabled={loading}
            >
              {loading ? 'Submitting…' : '✅ Submit Feedback'}
            </button>
            <div style={{
              fontSize: 11, color: '#94a3b8', marginTop: 20, width: '100%', textAlign: 'center'
            }}>
              Powered by{' '}
              <span>
            <a href={'https://theperceptpulse.com'} target={'_blank'}
               style={{color: PRIMARY_COLOR, textDecoration: 'none'}}>
              Percept Pulse
            </a>
          </span>
            </div>
          </div>
      )}

      {/* ── AUDIO: Customer records ── */}
      {stage === 'audio' && (
          <div style={{maxWidth: 480}}>
            <div className="card" style={{textAlign: 'center', padding: '40px 32px'}}>
              <div style={{fontSize: 48, marginBottom: 12}}>🎙️</div>
              <h3 style={{marginBottom: 8}}>Record Your Feedback</h3>
              <p className="text-muted mb-3" style={{fontSize: 14}}>Tell us about your experience in your own words.</p>

              {error && <div className="alert alert-error">{error}</div>}

              {recording && (
                  <div style={{marginBottom: 20}}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      background: '#fee2e2',
                      borderRadius: 20,
                      padding: '8px 18px',
                      color: '#b91c1c',
                      fontWeight: 600
                    }}>
                      <span style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#dc2626',
                        animation: 'pulse 1s infinite'
                      }}/>
                      Recording… {audioDuration}s
                    </div>
                  </div>
              )}

              {audioUrl && !recording && (
                  <div style={{marginBottom: 20}}>
                    <audio controls src={audioUrl} style={{width: '100%', borderRadius: 8}}/>
                    <p style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 6}}>Duration: {audioDuration}s</p>
                  </div>
              )}

              <div style={{display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap'}}>
                {!recording && !audioBlob && (
                    <button className="btn btn-primary btn-lg" onClick={startRecording}>🎙️ Start Recording</button>
                )}
                {recording && (
                    <button className="btn btn-danger btn-lg" onClick={stopRecording}>⏹ Stop</button>
                )}
                {audioBlob && !recording && (
                    <>
                      <button className="btn btn-outline" onClick={() => {
                        setAudioBlob(null);
                        setAudioUrl(null);
                        setAudioDuration(0);
                      }}>🔄 Re-record
                      </button>
                      <button className="btn btn-primary btn-lg" onClick={handleAudioSubmit} disabled={loading}>
                        {loading ? 'Submitting…' : '✅ Submit'}
                      </button>
                    </>
                )}
              </div>
              {audioBlob && !recording && (
                  <CustomerCaptureForm branding={branding} onChange={setCustomerInfo}/>
              )}
            </div>
            <div style={{
              fontSize: 11, color: '#94a3b8', marginTop: 20, width: '100%', textAlign: 'center'
            }}>
              Powered by{' '}
              <span>
            <a href={'https://theperceptpulse.com'} target={'_blank'}
               style={{color: PRIMARY_COLOR, textDecoration: 'none'}}>
              Percept Pulse
            </a>
          </span>
            </div>
          </div>
      )}

      {/* ── DONE ── */}
      {stage === 'done' && (
          <div style={{maxWidth: 480}}>
            <div className="card text-center" style={{
              padding: '56px 40px',
              background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
              border: '1px solid #86efac'
            }}>
              <div style={{fontSize: 64, marginBottom: 16}}>🎉</div>
              <h2 style={{marginBottom: 10, color: PRIMARY_COLOR}}>Thank You!</h2>
              <p className="text-muted mb-3">Your feedback has been received. We appreciate your time!</p>
              <button className="btn btn-primary" onClick={reset}>
                ➕ Next Customer
              </button>
            </div>
            <div style={{
              fontSize: 11, color: '#94a3b8', marginTop: 20, width: '100%', textAlign: 'center'
            }}>
              Powered by{' '}
              <span>
            <a href={'https://theperceptpulse.com'} target={'_blank'}
               style={{color: PRIMARY_COLOR, textDecoration: 'none'}}>
              Percept Pulse
            </a>
          </span>
            </div>
          </div>
      )}
    </div>
  );

  // ── History tab ──────────────────────────────────────────────────────────
  const renderHistoryTab = () => (
      <div className="table-wrap">
        <table>
          <thead>
          <tr>
            <th>Bill ID</th>
            <th>Type</th>
            <th>Status</th>
            <th>Time</th>
          </tr>
          </thead>
          <tbody>
          {history.length === 0 && (
            <tr><td colSpan={4} className="text-center text-muted" style={{ padding: 40 }}>No sessions yet</td></tr>
          )}
          {history.map(s => (
            <tr key={s._id}>
              <td>{s.billId || '—'}</td>
              <td>{s.feedbackType ? <span className={`badge ${s.feedbackType === 'audio' ? 'badge-info' : 'badge-gray'}`}>{s.feedbackType}</span> : <span className="badge badge-gray">—</span>}</td>
              <td><span className={`badge ${s.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>{s.status}</span></td>
              <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(s.createdAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="page">
      <nav className="navbar">
        <div className="container navbar-inner">
          {/*<span className="navbar-brand">💬 FeedbackApp</span>*/}
          <img src={'https://pub-267dd4744ace49cf8306de8e7c530c15.r2.dev/assets/Logo-Color.png'} style={{width: 100}}/>
          <div className="navbar-actions">
            <span style={{fontSize: 14, color: 'var(--text-muted)'}}>{user?.businessName} · {user?.name}</span>
            {(stage === 'idle' || stage === 'done') && (
                <button className="btn btn-outline btn-sm" onClick={handleLogout}>Logout</button>
            )}
          </div>
        </div>
      </nav>

      <div className="container" style={{paddingTop: 32}}>
        {/* Only show tabs when idle or done — hide during active customer flow */}
        {(stage === 'idle' || stage === 'done') && (
          <div className="tabs" style={{ maxWidth: 400 }}>
            <button className={`tab ${tab === 'new' ? 'active' : ''}`} onClick={() => { setTab('new'); reset(); }}>➕ New Session</button>
            <button className={`tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>📋 History</button>
          </div>
        )}

        {tab === 'new' || stage !== 'idle' ? renderStaffTab() : renderHistoryTab()}
        {tab === 'history' && stage === 'idle' && renderHistoryTab()}
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>

      <div style={{height: 50}}/>
      <PWAInstallPrompt />
    </div>
  );
}
